import { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import { updateUserData } from "../../app/auth/authSlice";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import moment from "moment";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import DiscountRuleModal from "./discountRuleModel";
import CustomerGroupSelectionModal from "../../components/CustomComponents/modal/customerGroupSelectionModel";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv";
import CombinedDateTimePicker from "../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";
import {
  parseCompanyDateTimeForPicker,
  toCompanyWallClockDateTimeString,
} from "../../utils/companyDateTime";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import useStableFetchEffect from "../../hooks/useStableFetchEffect";

function UpdateDiscount() {
  const { discountId } = useParams();
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompID = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const compTimeZone = profileData?.compTimeZone ?? profileData?.CompTimeZone ?? "UTC";
  const compUTCTimeZone = profileData?.compUTCTimeZone ?? profileData?.CompUTCTimeZone ?? "";
  const navigate = useNavigate();
  const isBranchMode = useIsChildBranch();
  const [isParentCompanyDiscount, setIsParentCompanyDiscount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [discountData, setDiscountData] = useState({
    name: "",
    amountType: "Percentage (%)",
    amount: "",
    enableRules: false,
    quantity: "",
    enableMinSpend: false,
    minSubtotal: "",
    enableMaxDiscount: false,
    maxDiscountValue: "",
    requirePasscode: false,
    applyDiscountAfterTaxes: false,
  });
  const [addAllItems, setAddAllItems] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showCustomerGroupModal, setShowCustomerGroupModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState("Category");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [enableDateRange, setEnableDateRange] = useState(false);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "12:00 AM", to: "11:59 PM" },
  ]);
  const [dateRange, setDateRange] = useState({
    startDateTime: null,
    endDateTime: null,
  });

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Fetch CompCurrency
  const fetchCompCurrency = useCallback(async () => {
    try {
      // First, try to get from Redux state
      if (profileData) {
        setCompCurrency(profileData?.compCurrency || "GBP");
        return;
      }

      // If not in Redux, try to fetch from API
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
        if (response.data?.data) {
          const currency = response.data.data.compCurrency || "GBP";
          setCompCurrency(currency);
          // Update Redux with fetched data
          dispatch(updateUserData({ profileData: response.data.data }));
          return;
        }
      } catch (apiErr) {
        console.error("Failed to fetch profile from API:", apiErr);
      }

      // If both fail, use default but don't show error toast on initial load
      setCompCurrency("GBP");
    } catch (err) {
      console.error("Error in fetchCompCurrency:", err);
      setCompCurrency("GBP"); // Use default instead of showing error
    }
  }, [profileData?.compCurrency, dispatch]);

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDiscountData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setDiscountData((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === "enableMinSpend" && !checked ? { minSubtotal: "" } : {}),
      ...(name === "enableMaxDiscount" && !checked ? { maxDiscountValue: "" } : {}),
    }));
    if (name === "enableRules" && checked) {
      setShowRuleModal(true);
    }
    if (name === "enableMinSpend" && !checked) {
      setDiscountData((prev) => ({ ...prev, minSubtotal: "" }));
    }
    if (name === "enableMaxDiscount" && !checked) {
      setDiscountData((prev) => ({ ...prev, maxDiscountValue: "" }));
    }
  };

  const getAmountPlaceholder = () => {
    return "Percentage (e.g. 10%)";
  };

  const getRuleText = () => {
    if (selectedRule === "Category" && discountData.quantity) {
      return `When customer purchases any ${discountData.quantity} or more items from ${selectedRule.toLowerCase()}`;
    }
    return `When customer purchases any item from ${selectedRule.toLowerCase()}`;
  };

  const fetchCategories = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      setCategories(response.data.data || response.data);
    } catch (err) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const fetchProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });
      setProducts(response.data.data || response.data);
    } catch (err) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const fetchDiscount = useCallback(async () => {
    const companyTimezoneOptions = {
      compTimeZone: profileData?.compTimeZone ?? profileData?.CompTimeZone ?? "UTC",
      compUTCTimeZone:
        profileData?.compUTCTimeZone ?? profileData?.CompUTCTimeZone ?? "",
    };

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_DISCOUNT_BY_ID, {
        params: { CompID, DiscountID: discountId },
      });
      const discount = response.data.data || response.data;

      // Check if this is a branch and the discount belongs to parent company
      if (isBranchMode && discount) {
        const discountCompID = discount.CompID || discount.compID;
        const parentCompID = profileData?.parentComID || profileData?.ParentComID || 0;
        
        // If discount's CompID matches parent CompID, it belongs to parent company
        if (discountCompID && parentCompID && discountCompID === parentCompID) {
          setIsParentCompanyDiscount(true);
        }
      }

      const uniqueProducts = Array.from(
        new Map(
          (discount.discountEntryItems || []).map((item) => [
            item.menuItem_ID,
            item,
          ])
        ).values()
      );
      const uniqueCategories = Array.from(
        new Map(
          (discount.discountEntryCategories || []).map((cat) => [
            cat.rootCat_ID,
            cat,
          ])
        ).values()
      );

      setDiscountData({
        name: discount.discountName || "",
        amountType: "Percentage (%)",
        amount:
          discount.amountType === "Percentage"
            ? discount.discountPercentage
            : discount.orderAmount,
        enableRules: discount.isDiscountRulesActive || false,
        quantity: discount.discountApplyWhenQTYofItems || "",
        enableMinSpend: discount.isSetMinimumSpend || false,
        minSubtotal: discount.minimumSpendAmount || "",
        enableMaxDiscount: discount.isSetMaxDiscountValue || false,
        maxDiscountValue: discount.maxDiscountValue || "",
        requirePasscode: discount.isRequirePassCode || false,
        applyDiscountAfterTaxes: discount.isDiscountafterTax || false,
      });

      setAddAllItems(discount.isDiscountApplyOnAllItems || false);
      setSelectedRule(discount.discountRuleType || "Category");
      setSelectedProducts(uniqueProducts);
      setSelectedCategories(uniqueCategories);
      setSelectedCustomerGroups(discount.discountEntryCustomerGroups || []);
      setEnableSchedule(discount.isScheduleDiscount || false);
      setEnableDateRange(discount.isSetDateRange || false);

      if (
        discount.discountWeeklyTimings &&
        Array.isArray(discount.discountWeeklyTimings) &&
        discount.discountWeeklyTimings.length > 0
      ) {
        setEnableSchedule(true);
        const isCommon = checkIfCommonTimeSlots(discount.discountWeeklyTimings);
        if (isCommon) {
          const firstDaySlots = discount.discountWeeklyTimings
            .filter(
              (day) => day.dayName === discount.discountWeeklyTimings[0].dayName
            )
            .map((day) => ({
              from: day.openTime,
              to: day.closeTime,
            }));
          setCommonTimeSlot(firstDaySlots);
        } else {
          setUseSameTimeSlots(false);
        }
        const convertedSchedules = convertToScheduleFormat(
          discount.discountWeeklyTimings
        );
        setSchedules(convertedSchedules);
      } else {
        setSchedules(schedule);
      }

      if (discount.isSetDateRange) {
        setDateRange({
          startDateTime: discount.discountStartDate
            ? parseCompanyDateTimeForPicker(
                discount.discountStartDate,
                companyTimezoneOptions
              )
            : null,
          endDateTime: discount.discountEndDate
            ? parseCompanyDateTimeForPicker(
                discount.discountEndDate,
                companyTimezoneOptions
              )
            : null,
        });
      } else {
        setDateRange({
          startDateTime: null,
          endDateTime: null,
        });
      }
    } catch (err) {
      showErrorToast(`Failed to load discount data: ${err.message}`);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [CompID, discountId, isBranchMode, parentCompID, compTimeZone, compUTCTimeZone]);

  useEffect(() => {
    if (!CompID) return;
    fetchCategories();
    fetchProducts();
  }, [CompID, fetchCategories, fetchProducts]);

  useStableFetchEffect(async (isStale) => {
    await fetchCompCurrency();
    if (isStale()) return;
    await fetchDiscount();
  }, [CompID, discountId, parentCompID, isBranchMode, profileData?.compCurrency, compTimeZone, compUTCTimeZone]);

  const handleProductSelection = (selectedProductItems) => {
    const uniqueProducts = Array.from(
      new Map(
        selectedProductItems.map((item) => [item.menuItem_ID, item])
      ).values()
    );
    setSelectedProducts(uniqueProducts);
    setShowProductModal(false);
  };

  const handleCategorySelection = (selectedCat) => {
    const uniqueCategories = Array.from(
      new Map(selectedCat.map((cat) => [cat.rootCat_ID, cat])).values()
    );
    setSelectedCategories(uniqueCategories);
    setShowCategoryModal(false);
  };

  const handleRuleSelection = (rule) => {
    setSelectedRule(rule);
    if (rule === "Customer group") {
      setShowCustomerGroupModal(true);
      setSelectedProducts([]);
      setSelectedCategories([]);
      setAddAllItems(false);
    } else {
      setDiscountData((prev) => ({
        ...prev,
        enableMinSpend: false,
        minSubtotal: "",
      }));
      setSelectedCustomerGroups([]);
    }
  };

  const handleCustomerGroupSelection = (groups) => {
    setSelectedCustomerGroups(groups);
  };

  const removeProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.filter((p) => p.menuItem_ID !== productId)
    );
  };

  const removeCategory = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.filter((c) => c.rootCat_ID !== categoryId)
    );
  };

  const removeCustomerGroup = (groupId) => {
    setSelectedCustomerGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => {
      const momentValue = value && moment.isMoment(value) && value.isValid() ? value : null;
      const newDateRange = { ...prev, [field]: momentValue };

      if (
        field === "startDateTime" &&
        newDateRange.endDateTime &&
        moment.isMoment(newDateRange.endDateTime) &&
        newDateRange.endDateTime.isValid()
      ) {
        if (newDateRange.endDateTime.isBefore(momentValue)) {
          newDateRange.endDateTime = momentValue ? momentValue.clone() : null;
        }
      }

      return newDateRange;
    });
  };

  // Note: These validation functions are no longer used since we removed isValidDate props
  // The CombinedDateTimePicker now uses its built-in default validation

  const handleEnableDateRangeToggle = (checked) => {
    if (checked) {
      const now = moment();
      setDateRange({
        startDateTime: now,
        endDateTime: now,
      });
    } else {
      setDateRange({
        startDateTime: null,
        endDateTime: null,
      });
    }
    setEnableDateRange(checked);
  };

  const handleSubmit = async () => {
    // Prevent update if this is a branch viewing a parent company discount
    if (isBranchMode && isParentCompanyDiscount) {
      return;
    }
    if (!discountData.name.trim()) {
      showErrorToast("Discount name is required");
      return;
    }

    if (enableDateRange) {
      if (!dateRange.startDateTime || !moment(dateRange.startDateTime).isValid()) {
        showErrorToast("Please select a valid start date and time");
        return;
      }
      if (!dateRange.endDateTime || !moment(dateRange.endDateTime).isValid()) {
        showErrorToast("Please select a valid end date and time");
        return;
      }
      if (moment(dateRange.endDateTime).isBefore(dateRange.startDateTime)) {
        showErrorToast("End date and time cannot be before start date and time");
        return;
      }
    }

    setLoading(true);

    try {
      let startDateTime = null;
      let endDateTime = null;
      if (enableDateRange) {
        if (dateRange.startDateTime && moment(dateRange.startDateTime).isValid()) {
          startDateTime = toCompanyWallClockDateTimeString(dateRange.startDateTime);
        }
        if (dateRange.endDateTime && moment(dateRange.endDateTime).isValid()) {
          endDateTime = toCompanyWallClockDateTimeString(dateRange.endDateTime);
        }
      }

      const formattedSchedules = [];
      if (enableSchedule) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  DiscountID: parseInt(discountId),
                  DayName: day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    DiscountID: parseInt(discountId),
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const discountPayload = {
        ID: parseInt(discountId),
        CompID: CompID,
        DiscountName: discountData.name,
        AmountType: "Percentage",
        DiscountPercentage: parseFloat(discountData.amount) || 0,
        OrderAmount: 0,
        IsDiscountRulesActive: discountData.enableRules,
        DiscountRuleType: selectedRule,
        DiscountApplyWhenQTYofItems:
          selectedRule === "Category"
            ? parseInt(discountData.quantity) || 0
            : 0,
        IsDiscountApplyOnAllItems: addAllItems,
        IsSetMinimumSpend: discountData.enableMinSpend,
        MinimumSpendAmount: discountData.enableMinSpend
          ? parseFloat(discountData.minSubtotal) || 0
          : 0,
        IsSetMaxDiscountValue: discountData.enableMaxDiscount,
        MaxDiscountValue: discountData.enableMaxDiscount
          ? parseFloat(discountData.maxDiscountValue) || 0
          : null,
        IsRequirePassCode: discountData.requirePasscode,
        IsDiscountafterTax: discountData.applyDiscountAfterTaxes,
        IsScheduleDiscount: enableSchedule,
        IsSetDateRange: enableDateRange,
        DiscountStartDate: startDateTime || null,
        DiscountEndDate: endDateTime || null,
        IsAutoApplyonPOS: false,
        IsAutoApplyonOnlineSites: false,
        UpdateDateTime: new Date().toISOString(),
        DiscountWeeklyTimings: formattedSchedules,
        DiscountEntryItems:
          discountData.enableRules &&
          selectedRule === "Category" &&
          !addAllItems
            ? selectedProducts
            : [],
        DiscountEntryCategories:
          discountData.enableRules &&
          selectedRule === "Category" &&
          !addAllItems
            ? selectedCategories
            : [],
        DiscountEntryCustomerGroups:
          discountData.enableRules && selectedRule === "Customer group"
            ? selectedCustomerGroups
            : [],
      };

      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_DISCOUNT,
        discountPayload
      );

      if (!response.data.status) {
        showErrorToast(response.data.Message || "Failed to update discount");
        return;
      }

      showSuccessToast("Discount updated successfully");
      navigate("/discount/manageDiscount");
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message ||
          "An error occurred while updating the discount"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {loading && !initialLoading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/discount/manageDiscount">Manage discounts</Link>
            </li>
            <li className="breadcrumb-item active">Update Discount</li>
          </ol>
          <h1 className="page-header mb-0">Update Discount</h1>
        </div>
        {!(isBranchMode && isParentCompanyDiscount) && (
          <div className="ms-auto">
            <button
              className="btn px-4"
              onClick={handleSubmit}
              disabled={loading}
              style={{ 
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              <i className="fa-lg me-2 ms-n2 text-white"></i>
              Update
            </button>
          </div>
        )}
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>

          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <div
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isBranchMode && isParentCompanyDiscount) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <div className="row mb-4">
                  <div className="col-12">
                    <FormInput
                      label="Name"
                      placeHolder="Discount name"
                      type="text"
                      name="name"
                      value={discountData.name}
                      onchange={handleInputChange}
                      disabled={isBranchMode && isParentCompanyDiscount}
                    />
                  </div>
                </div>

                <div className="row mb-4">
                  {/* Discount type - Hidden (always Percentage) */}
                  {/* 
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label className="form-label">Discount type</label>
                    <div
                      className="select-wrapper"
                      style={{ position: "relative" }}
                    >
                      <select
                        className="form-control"
                        name="amountType"
                        value={discountData.amountType}
                        disabled
                        style={{
                          borderRadius: "10px",
                          border: isDarkMode ? "1px solid #4b5e6f" : "1px solid #ced4da",
                          background: isDarkMode ? "#343a40" : "#fff",
                          color: isDarkMode ? "#ced4da" : "#495057",
                          height: "44px",
                          paddingRight: "40px",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                        }}
                      >
                        <option>Percentage (%)</option>
                      </select>
                      <div
                        className="chevron-icons"
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          pointerEvents: "none",
                        }}
                      >
                        <i
                          className="fas fa-chevron-down"
                          style={{
                            fontSize: "10px",
                            color: isDarkMode ? "#ced4da" : "#495057",
                          }}
                        ></i>
                      </div>
                    </div>
                  </div>
                  */}
                  <div className="col-md-6">
                    <FormInput
                      label="Percentage"
                      placeHolder="Percentage (e.g. 10%)"
                      type="number"
                      name="amount"
                      value={discountData.amount}
                      onchange={handleInputChange}
                      disabled={isBranchMode && isParentCompanyDiscount}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <ToggleSwitch
                      isChecked={discountData.enableRules}
                      onToggle={(checked) => handleToggleChange(checked, "enableRules")}
                      label="Discount rules"
                      name="enableRules"
                      themeColor={themeColor}
                    />
                    <p className="text-muted mt-2">
                      Apply this discount automatically to specific items,
                      categories, or customer groups.
                    </p>
                  </div>
                </div>

                {discountData.enableRules && (
                  <>
                    <div className="row py-3">
                      <div className="col-12">
                        <div
                          className="row py-3"
                          style={{ 
                            backgroundColor: isDarkMode ? '#3c4043' : '#f5f5f5',
                            borderRadius: '8px',
                            border: isDarkMode ? '1px solid #404040' : '1px solid #e9ecef'
                          }}
                        >
                          <div className="col-12 d-flex justify-content-between align-items-center">
                            <h6 style={{ 
                              color: isDarkMode ? '#ffffff' : '#212529',
                              margin: 0
                            }}>
                              {getRuleText()}
                            </h6>
                            <button
                              className="btn btn-link"
                              onClick={() => setShowRuleModal(true)}
                              style={{
                                color: themeColor,
                                textDecoration: 'none',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                              }}
                            >
                              Edit purchase rule
                            </button>
                          </div>
                        </div>

                        <div className="row py-3">
                          <div className="col-12">
                            {selectedRule === "Category" && (
                              <>
                                <div className="row mb-3">
                                  <div className="col-md-6">
                                    <FormInput
                                      label="Minimum Quantity"
                                      placeHolder="Minimum quantity"
                                      type="number"
                                      name="quantity"
                                      value={discountData.quantity}
                                      onchange={handleInputChange}
                      disabled={isBranchMode && isParentCompanyDiscount}
                                    />
                                    <p className="text-muted mt-2">
                                      Minimum quantity required for discount to
                                      apply
                                    </p>
                                  </div>
                                </div>

                                <label className="form-label">Apply to Categories</label>
                                <div className="d-flex align-items-center mb-3">
                                  <button
                                    className="btn btn-link text-primary"
                                    onClick={() => setShowCategoryModal(true)}
                                    disabled={addAllItems}
                                  >
                                    Add categories
                                  </button>
                                </div>

                                {selectedCategories.length > 0 && !addAllItems && (
                                  <div className="mt-3">
                                    <h6>Selected Categories:</h6>
                                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                      {selectedCategories.map((category) => (
                                        <div
                                          key={`category-${category.rootCat_ID}`}
                                          className="col"
                                        >
                                          <div className="card h-100">
                                            <div className="card-body d-flex justify-content-between">
                                              <span>{category.rootCat_Name}</span>
                                              <i
                                                className="fa fa-trash text-danger"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  removeCategory(category.rootCat_ID)
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {selectedProducts.length > 0 && !addAllItems && (
                                  <div className="mt-3">
                                    <h6>Selected Products:</h6>
                                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                      {selectedProducts.map((product, index) => (
                                        <div
                                          key={`product-${product.menuItem_ID}-${index}`}
                                          className="col"
                                        >
                                          <div className="card h-100">
                                            <div className="card-body d-flex justify-content-between align-items-center">
                                              <div className="d-flex align-items-center">
                                                {product.menuItem_Image ? (
                                                  <img
                                                    src={product.menuItem_Image}
                                                    alt={product.menuItem_Name}
                                                    style={{
                                                      width: "30px",
                                                      height: "30px",
                                                      objectFit: "contain",
                                                      marginRight: "10px",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "30px",
                                                      height: "30px",
                                                      backgroundColor: isDarkMode ? "#495057" : "#d9d9d9",
                                                      marginRight: "10px",
                                                    }}
                                                  />
                                                )}
                                                <span>{product.menuItem_Name}</span>
                                              </div>
                                              <i
                                                className="fa fa-trash text-danger"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  removeProduct(product.menuItem_ID)
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {addAllItems && (
                                  <div className="mt-3">
                                    <p className="text-muted">
                                      Discount will be applied to all items.
                                    </p>
                                  </div>
                                )}
                              </>
                            )}

                            {selectedRule === "Customer group" && (
                              <div className="mt-3">
                                <label className="form-label">
                                  Apply to customer groups
                                </label>
                                <div className="mb-3">
                                  <button
                                    className="btn btn-link text-primary"
                                    onClick={() => setShowCustomerGroupModal(true)}
                                  >
                                    Select customer groups
                                  </button>
                                </div>

                                {selectedCustomerGroups.length > 0 && (
                                  <div className="mt-3">
                                    <h6>Selected Customer Groups:</h6>
                                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                      {selectedCustomerGroups.map((group) => (
                                        <div key={`group-${group.id}`} className="col">
                                          <div className="card h-100">
                                            <div className="card-body d-flex justify-content-between align-items-center">
                                              <span>{group.name}</span>
                                              <i
                                                className="fa fa-trash text-danger"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  removeCustomerGroup(group.id)
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row py-3">
                          <div className="col-12">
                            <h5>Discount schedule</h5>
                            <div className="mb-3">
                              <ToggleSwitch
                                isChecked={enableSchedule}
                                onToggle={() => setEnableSchedule(!enableSchedule)}
                                label="Set schedule"
                                name="enableSchedule"
                                themeColor={themeColor}
                              />
                              <p className="text-muted mt-2">
                                Set the days of the week and times of day this
                                discount is available. (Example: Happy Hour)
                              </p>
                            </div>

                            {enableSchedule && (
                              <div className="mb-4">
                                <div className="mb-4 mt-3">
                                  <ToggleSwitch
                                    isChecked={useSameTimeSlots}
                                    onToggle={() => {
                                      setUseSameTimeSlots(!useSameTimeSlots);
                                      if (!useSameTimeSlots) {
                                        applyCommonTimeSlotToAll();
                                      }
                                    }}
                                    label="Use same time slots for all days"
                                    disabled={false}
                                    themeColor={themeColor}
                                  />
                                </div>
                                <hr />

                                {useSameTimeSlots ? (
                                  <CommonTimeSlotForAllWeek
                                    setCommonTimeSlots={setCommonTimeSlot}
                                    commonTimeSlots={commonTimeSlot}
                                    isProfilePage={false}
                                  />
                                ) : (
                                  <ScheduleManager
                                    schedules={schedules}
                                    setSchedules={setSchedules}
                                    isProfilePage={false}
                                    isDarkMode={isDarkMode}
                                  />
                                )}
                              </div>
                            )}

                            <div className="mb-3">
                              <ToggleSwitch
                                isChecked={enableDateRange}
                                onToggle={handleEnableDateRangeToggle}
                                label="Set date range"
                                name="enableDateRange"
                                themeColor={themeColor}
                              />
                              <p className="text-muted mt-2">
                                Set the dates the discount is available. (Example:
                                Seasonal Sale)
                              </p>
                            </div>

                            {enableDateRange && (
                              <div className="row mb-3">
                                <div className="col-md-6 mb-3">
                                  <CombinedDateTimePicker
                                    label="Start Date & Time"
                                    value={dateRange.startDateTime}
                                    onChange={(date) =>
                                      handleDateRangeChange("startDateTime", date)
                                    }
                                    ampm={true}
                                    placeholder="Select start date and time"
                                    name="startDateTime"
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <CombinedDateTimePicker
                                    label="End Date & Time"
                                    value={dateRange.endDateTime}
                                    onChange={(date) =>
                                      handleDateRangeChange("endDateTime", date)
                                    }
                                    ampm={true}
                                    placeholder="Select end date and time"
                                    name="endDateTime"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row py-3">
                          <div className="col-12">
                            <h5>Minimum spend</h5>
                            <div className="mb-3">
                              <ToggleSwitch
                                isChecked={discountData.enableMinSpend}
                                onToggle={(checked) => handleToggleChange(checked, "enableMinSpend")}
                                label="Set minimum spend"
                                name="enableMinSpend"
                                disabled={selectedRule !== "Customer group"}
                                themeColor={themeColor}
                              />
                              <p className="text-muted mt-2">
                                Require a minimum subtotal to qualify for discount
                                (Example: Buy at least {getCurrencySymbol(compCurrency)}50 and get 10% off)
                                {selectedRule !== "Customer group" && (
                                  <span className="text-danger">
                                    {
                                      " (Only available for Customer Group Discounts)"
                                    }
                                  </span>
                                )}
                              </p>
                            </div>

                            {discountData.enableMinSpend && (
                              <div className="row mb-3">
                                <div className="col-md-6">
                                  <FormInput
                                    label="Minimum subtotal"
                                    placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                                    type="number"
                                    name="minSubtotal"
                                    value={discountData.minSubtotal}
                                    onchange={handleInputChange}
                      disabled={isBranchMode && isParentCompanyDiscount}
                                    prepend={getCurrencySymbol(compCurrency)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className="row py-3">
                  <div className="card col-12">
                    <h5>Options</h5>
                    <div className="mb-3">
                      <ToggleSwitch
                        isChecked={discountData.enableMaxDiscount}
                        onToggle={(checked) => handleToggleChange(checked, "enableMaxDiscount")}
                        label="Set maximum discount value"
                        name="enableMaxDiscount"
                        themeColor={themeColor}
                      />
                      <p className="text-muted mt-2">
                        Set the maximum discount value per purchase
                      </p>
                    </div>

                    {discountData.enableMaxDiscount && (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <FormInput
                            label={`Maximum discount value (${getCurrencySymbol(compCurrency)})`}
                            placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                            type="number"
                            name="maxDiscountValue"
                            value={discountData.maxDiscountValue}
                            onchange={handleInputChange}
                      disabled={isBranchMode && isParentCompanyDiscount}
                            prepend={getCurrencySymbol(compCurrency)}
                          />
                        </div>
                      </div>
                    )}
                    <span className="text-muted">
                      Maximum discount value can only apply to percentage-based
                      discounts.
                    </span>

                    <div className="mb-3 mt-3 d-flex justify-content-between">
                      <div className="me-4">
                        <ToggleSwitch
                          isChecked={discountData.requirePasscode}
                          onToggle={(checked) => handleToggleChange(checked, "requirePasscode")}
                          label="Require passcode"
                          name="requirePasscode"
                          themeColor={themeColor}
                        />
                        <p className="text-muted mt-2">
                          Require a passcode to apply this discount at checkout
                        </p>
                      </div>

                      <div>
                        <ToggleSwitch
                          isChecked={discountData.applyDiscountAfterTaxes}
                          onToggle={(checked) => handleToggleChange(checked, "applyDiscountAfterTaxes")}
                          label="Apply discount after taxes"
                          name="applyDiscountAfterTaxes"
                          themeColor={themeColor}
                        />
                        <p className="text-muted mt-2">
                          This should only be used for manufacturer discounts and
                          instant rebates
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>

      {!initialLoading && (
        <ProductMultiSelectionModal
          showModal={showProductModal}
          setShowModal={setShowProductModal}
          showButton={true}
          onSubmit={handleProductSelection}
          setSelectedProducts={setSelectedProducts}
          selectedProducts={selectedProducts}
          products={products}
        />
      )}

      {!initialLoading && (
        <CategoryModal
          showParentCategoryModal={showCategoryModal}
          setShowParentCategoryModal={setShowCategoryModal}
          showButton={true}
          setSelectedParentCategory={setSelectedCategories}
          selectedParentCategory={selectedCategories}
          onSubmit={handleCategorySelection}
          categories={categories}
          isMultiSelect={true}
          modalTitle="Select Category"
          modalSubTitle="Select the categories for the Discount"
        />
      )}

      {!initialLoading && (
        <DiscountRuleModal
          showModal={showRuleModal}
          setShowModal={setShowRuleModal}
          onSubmit={handleRuleSelection}
          selectedRule={selectedRule}
          setSelectedRule={setSelectedRule}
        />
      )}

      {!initialLoading && (
        <CustomerGroupSelectionModal
          showModal={showCustomerGroupModal}
          setShowModal={setShowCustomerGroupModal}
          onSubmit={handleCustomerGroupSelection}
          selectedGroups={selectedCustomerGroups}
          setSelectedGroups={setSelectedCustomerGroups}
        />
      )}
    </div>
  );
}

export default UpdateDiscount;